import { getAuthToken } from "../../../auth/authClient";
import { decryptFollowupE2E, followupE2EClient } from "./followupE2EClient";
import { fourEClient } from "./fourEClient";

const API_BASE = (process.env.REACT_APP_MORFEUSZ_4E_API_URL || "").replace(/\/$/, "");

const dreamsUrl = (path = "") => `${API_BASE}/dreams${path}`;

const getMorfeuszNonce = () => {
  if (typeof window === "undefined") return "";
  try {
    if (typeof window.morfeuszNonce === "function") return String(window.morfeuszNonce() || "");
    const field = document.getElementById("morfeusz_nonce");
    return String(field?.value || field?.getAttribute?.("value") || "");
  } catch (_) {
    return "";
  }
};

const requestHeaders = (options = {}) => {
  const token = getAuthToken();
  const nonce = getMorfeuszNonce();
  return {
    ...(options.body ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(nonce ? { "X-Morfeusz-Nonce": nonce } : {}),
    ...(options.headers || {}),
  };
};

const normalizeE2EPack = (payload = {}) => {
  const e2e = payload.e2e && typeof payload.e2e === "object" ? payload.e2e : {};
  const nested = e2e.dream && typeof e2e.dream === "object" ? e2e.dream : {};
  const iv = String(e2e.iv || nested.iv || payload.dream_iv || "");
  const ct = String(e2e.ct || nested.ct || payload.dream_ct || "");
  const aad = String(e2e.aad || nested.aad || payload.dream_aad || "");

  if (!iv || !ct) return null;

  return {
    iv,
    ct,
    aad,
    dream: {
      iv,
      ct,
      aad,
    },
  };
};

const stripPlainDreamFields = (dream) => {
  if (!dream || typeof dream !== "object") return dream;
  const next = { ...dream };
  next.saveMode = "e2e";
  next.save_mode = "e2e";
  next.bodyHtml = "";
  next.excerpt = "";
  delete next.sen_opis;
  delete next["content"];
  return next;
};

const hydrateFollowup = async (followup) => {
  if (!followup || typeof followup !== "object") return followup;
  return decryptFollowupE2E(followup).catch(() => followup);
};

const formatAccessUntil = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat("pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  } catch (_) {
    return String(value).slice(0, 10);
  }
};

const formatUsageCounter = (bucket, fallback) => {
  const access = bucket?.access && typeof bucket.access === "object" ? bucket.access : null;
  if (access?.active) {
    const until = formatAccessUntil(access.until || access.tier_until || access.all_until || "");
    const reserve = bucket?.left ?? fallback ?? "";
    const reserveText = reserve !== "" ? ` · po czasie ${reserve}` : "";
    return until ? `otwarte do ${until}${reserveText}` : `czas aktywny${reserveText}`;
  }
  return fallback ?? "";
};

const normalizeLive4EUsage = (response) => {
  const usage = response?.usage || null;
  if (!usage || typeof usage !== "object") return null;
  const left = usage.left && typeof usage.left === "object" ? usage.left : {};
  const fastLeft = left.fast ?? usage.fast?.left ?? "";
  const deepLeft = left.deep ?? usage.deep?.left ?? "";
  return {
    ...usage,
    left: {
      fast: formatUsageCounter(usage.fast, fastLeft),
      deep: formatUsageCounter(usage.deep, deepLeft),
    },
  };
};

const mergeLive4EUsageIntoDream = (dream, usage) => {
  if (!dream || typeof dream !== "object" || !usage) return dream;
  const currentAnalysis = dream.analysis && typeof dream.analysis === "object" ? dream.analysis : null;
  const analysis = currentAnalysis || {
    source: "backend_4e",
    mode: dream.analysisMode || "ai",
    tier: dream.analysisTier || "fast",
    modelTier: dream.analysisTier || "fast",
    model: "4E",
    html: "",
    plainText: "",
  };

  return {
    ...dream,
    analysis: {
      ...analysis,
      backendMeta: {
        ...(analysis.backendMeta || {}),
        usage,
      },
    },
  };
};

const hydrateDreamWithLive4EUsage = async (data) => {
  if (!data?.dream || typeof data.dream !== "object") return data;
  const response = await fourEClient.limits().catch(() => null);
  if (!response?.ok) return data;
  const usage = normalizeLive4EUsage(response);
  if (!usage) return data;
  return {
    ...data,
    dream: mergeLive4EUsageIntoDream(data.dream, usage),
  };
};

const sanitizeResponsePayload = async (data, options = {}) => {
  if (!data || typeof data !== "object") return data;
  let next = { ...data };
  if (Array.isArray(next.dreams)) {
    next.dreams = next.dreams.map(stripPlainDreamFields);
  }
  if (next.dream && typeof next.dream === "object") {
    next.dream = stripPlainDreamFields(next.dream);
    if (options.decryptFollowup && next.dream.followup) {
      next.dream.followup = await hydrateFollowup(next.dream.followup);
    }
  }
  if (options.hydrate4EUsage) {
    next = await hydrateDreamWithLive4EUsage(next);
  }
  return next;
};

const sanitizeWritePayload = (payload = {}, { bodyRequired = false } = {}) => {
  const next = { ...(payload || {}) };
  const pack = normalizeE2EPack(next);

  if (bodyRequired && !pack) {
    const error = new Error("Sen musi być zapisany w sejfie E2E.");
    error.code = "e2e_required";
    throw error;
  }

  if (pack) {
    next.e2e = pack;
    next.dream_iv = pack.iv;
    next.dream_ct = pack.ct;
    next.dream_aad = pack.aad;
  }

  next.saveMode = "e2e";
  next.save_mode = "e2e";
  next.bodyHtml = "";
  next.sen_opis = "";
  delete next["content"];
  return next;
};

const parseResponse = async (response, options = {}) => {
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.ok) {
    const error = new Error(data?.message || `dreams-http-${response.status}`);
    error.code = data?.code || `dreams-http-${response.status}`;
    error.status = response.status;
    throw error;
  }
  return sanitizeResponsePayload(data, options);
};

const request = async (path = "", options = {}) => {
  if (!API_BASE) {
    const error = new Error("dreams-api-missing");
    error.code = "dreams-api-missing";
    throw error;
  }

  const response = await fetch(dreamsUrl(path), {
    credentials: "include",
    ...options,
    headers: requestHeaders(options),
  });
  return parseResponse(response, options);
};

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const mergeFollowupIntoDream = (data, followup) => {
  if (!data?.dream || !followup) return data;
  return {
    ...data,
    dream: {
      ...data.dream,
      followup,
    },
  };
};

export const dreamsClient = {
  isConfigured: () => Boolean(API_BASE),
  list: () => request(""),
  get: (id) => request(`/${encodeURIComponent(id)}`, { decryptFollowup: true, hydrate4EUsage: true }),
  create: (dream) => request("", { method: "POST", body: JSON.stringify(sanitizeWritePayload(dream, { bodyRequired: true })) }),
  update: async (id, patch) => {
    const hasFollowup = hasOwn(patch, "followup");
    const followupPatch = patch?.followup;
    const followupOnly = hasFollowup && Object.keys(patch || {}).every((key) => ["followup", "saveMode", "save_mode"].includes(key));

    if (followupOnly && followupPatch && typeof followupPatch === "object") {
      const savedFollowup = await followupE2EClient.save(id, followupPatch);
      const fresh = await request(`/${encodeURIComponent(id)}`, { hydrate4EUsage: true });
      return mergeFollowupIntoDream(fresh, savedFollowup.followup);
    }

    const touchesBody = Boolean(
      patch && (
        hasOwn(patch, "bodyHtml")
        || hasOwn(patch, "sen_opis")
        || hasOwn(patch, "e2e")
        || hasOwn(patch, "dream_iv")
        || hasOwn(patch, "dream_ct")
      ),
    );

    const payload = touchesBody ? sanitizeWritePayload(patch, { bodyRequired: true }) : { ...(patch || {}), saveMode: "e2e", save_mode: "e2e" };
    const data = await request(`/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(payload), hydrate4EUsage: true });

    if (hasFollowup && followupPatch === null) {
      await followupE2EClient.remove(id).catch(() => null);
    }

    return data;
  },
  remove: (id) => request(`/${encodeURIComponent(id)}`, { method: "DELETE" }),
};